import { ApiError, Comment, PowderguideApiInterface } from './powderguide-api'
import {PowderguideAuthentication} from "./powderguide-authentication";
import {LanguageLabels, Translator} from "./translator";

interface TextWithTranslation {
    text: string,
    locale: string,
    language: string,
    label: string | null,
    triggerLabel: string,
}

interface TranslationData {
    original: TextWithTranslation,
    translated: null | TextWithTranslation,
}

export class PowderguideComments {

    public comments: Comment[] = [];
    public loading: boolean = true;
    public submitting: boolean = false;
    public error: string|null = null;
    private translator: Translator = new Translator({ });

    public get canDelete(): boolean {
        return this.authentication.user && this.authentication.user.is_admin;
    }

    constructor(
        private api: PowderguideApiInterface,
        private authentication: PowderguideAuthentication,
        private target: 'entry'|'conditions_report',
        private uuid: string,
    ) {}

    public async init(...languageLabels: LanguageLabels[]): Promise<void> {
        for (const languageLabel of languageLabels) {
            this.translator.addLabels(languageLabel);
        }

        try {
            this.loading = true;
            await this.authentication.init({ });
            this.comments = await this.api.loadComments(this.target, this.uuid);
            this.loading = false;
        } catch (e) {
            this.error = e.message;
        }
    }

    public async initLatest(number: number, ...languageLabels: LanguageLabels[]): Promise<void> {
        for (const languageLabel of languageLabels) {
            this.translator.addLabels(languageLabel);
        }

        try {
            this.loading = true;
            this.comments = await this.api.loadLatestComments(number);
            this.loading = false;
        } catch (e) {
            this.error = e.message;
        }
    }

    public async post(comment: string): Promise<boolean> {
        if (this.submitting) {
            this.error = this.translator.translate('errors.operation_in_progress');
            return false;
        }

        if (comment.trim().length < 3) {
            this.error = this.translator.translate('errors.comment_to_short');
            return false;
        }

        this.submitting = true;
        const user = this.api.getUser();
        if (!user) {
            this.error = this.translator.translate('errors.not_logged_in');
            this.submitting = false;
            return false;
        }

        try {
            await this.api.postComment(this.target, this.uuid, comment);
        } catch (e) {
            const status = e instanceof ApiError ? e.status : 0;
            const errorMessages = {
                429: this.translator.translate('errors.too_many_requests'),
                409: this.translator.translate('errors.duplicate_comment'),
            };
            this.error = errorMessages[status] ?? e.message;
            this.loading = false;
            this.submitting = false;

            return false;
        }

        await this.init({ });
        this.submitting = false;

        return true;
    }

    public async delete(uuid: string): Promise<void> {
        this.loading = true;
        if (!this.authentication.user || !this.authentication.user.is_admin) {
            this.error = this.translator.translate('errors.cannot_delete');
        }

        await this.api.deleteComment(uuid);
        await this.init({ });
    }

    public translatedFromLabel(language: string): string {
        return this.translator.translate('translated_from', {
            language: this.translator.translate(language),
        })
    }

    public translationData(comment: Comment, locale: string): TranslationData {
        let translated: TextWithTranslation|null = null;
        const sourceLanguage = this.translator.translate(comment.language);

        if (this.isTranslation(comment, locale)) {
            translated = {
                text: comment.translations[locale].comment,
                locale: locale,
                language: this.translator.translate(locale),
                label: this.translator.translate('translated_from', {
                    language: sourceLanguage,
                }),
                triggerLabel: this.translator.translate('show_translated'),
            };
        }

        return {
            original: {
                text: comment.comment,
                locale: comment.language,
                language: sourceLanguage,
                label: null,
                triggerLabel: this.translator.translate('show_original'),
            },
            translated: translated,
        }
    }

    private isTranslation(comment: Comment, locale: string): boolean {
        if (comment.language === locale) {
            return false;
        }

        if (!comment.translations[locale] || !comment.translations[locale].comment) {
            return false;
        }

        // Prevent showing translations for stuff like `59 cm` and `59cm.`.
        const normalizedComment = comment.comment.replace(/\W/g, '').toLowerCase();
        const normalizedTranslation = `${comment.translations[locale].comment}`.replace(/\W/g, '').toLowerCase();
        if (normalizedComment === normalizedTranslation) {
            return false;
        }

        return true;
    }
}
